// deps
import React, { memo, useCallback, useMemo } from "react";

// internal deps
import { RenderingDetails } from "@swiggy-private/cart-types";
import {
    BillDetailItemDisplay,
    BillDetailItemInfoDisplay,
    BillDetailItemSeparator,
    BillDetailItemTitle,
    BillDetailItemTooltipDisplay,
    TransparentLine,
    TransparentLineV2,
    CollapsableBillDetailsWrapper,
    VALID_RENDERING_DETAILS_TYPE,
    getBillDetails,
    canShowTooltip,
} from "@swiggy-private/core-ui";

// types
import { IBillDetailsRendererProps } from "./types";

// styles
import { BillDetailCartGroupContainer } from "./BillDetailsRenderer.style";
import { isItemValueRupee } from "../../helpers";

const BillDetailsRenderer = ({
    renderingDetails,
    planAddedRef,
    planDetails,
    billDetailsRendererData,
    billDetailsAnalytics,
}: IBillDetailsRendererProps) => {
    const billDetails = useMemo(() => getBillDetails(renderingDetails), [renderingDetails]);

    const { onImpressionBanner, onClickBanner, onImpressionBannerHalfcard, onClickCloseBannerHalfcard } =
        billDetailsAnalytics;

    const { displayData, tooltipDisplayData, titleData, collapsibleWidgetData } = billDetailsRendererData;

    const renderBillDetailItem = useCallback(
        (itemData: RenderingDetails) => {
            switch (itemData.type.toUpperCase()) {
                case VALID_RENDERING_DETAILS_TYPE.DISPLAY:
                    return <BillDetailItemDisplay itemData={itemData} planAddedRef={planAddedRef} {...displayData} />;

                case VALID_RENDERING_DETAILS_TYPE.TOOLTIPDISPLAY:
                    return (
                        <BillDetailItemTooltipDisplay
                            itemData={itemData}
                            planAddedRef={planAddedRef}
                            planDetails={planDetails}
                            {...tooltipDisplayData}
                            isItemValueRupee={isItemValueRupee}
                            canShowTooltip={canShowTooltip}
                        />
                    );

                case VALID_RENDERING_DETAILS_TYPE.INFODISPLAY:
                    return <BillDetailItemInfoDisplay itemData={itemData} />;

                case VALID_RENDERING_DETAILS_TYPE.SEPARATOR:
                    return <BillDetailItemSeparator />;

                case VALID_RENDERING_DETAILS_TYPE.TITLE:
                    return <BillDetailItemTitle itemData={itemData} {...titleData} />;

                case VALID_RENDERING_DETAILS_TYPE.TRANSPARENT_SEPARATOR:
                    return <TransparentLine />;

                // this key signifies the end of the bill details section, if it's not present grand_total will be used as the end key
                case VALID_RENDERING_DETAILS_TYPE.BILL_DETAILS_SECTION_END:
                    return null;
                default:
                    return null;
            }
        },
        [
            displayData,
            onClickBanner,
            onClickCloseBannerHalfcard,
            onImpressionBanner,
            onImpressionBannerHalfcard,
            planAddedRef,
            planDetails,
            titleData,
            tooltipDisplayData,
        ],
    );

    return (
        <>
            <TransparentLineV2 />
            <div id="bill-details-renderer-container">
                <BillDetailCartGroupContainer isCollapsible={!collapsibleWidgetData.doNotShowCollapsableBill}>
                    <CollapsableBillDetailsWrapper {...collapsibleWidgetData}>
                        {billDetails?.children?.map((subItem: RenderingDetails) => renderBillDetailItem(subItem))}
                    </CollapsableBillDetailsWrapper>
                </BillDetailCartGroupContainer>
            </div>
        </>
    );
};

export default memo(BillDetailsRenderer);
